import React from "react";
import { FiClock, FiExternalLink, FiGlobe, FiSave, FiTrash2, FiZap } from "react-icons/fi";
import { LoadingSpinner } from "../LoadingSpinner";

type PageEditorHeaderActionsProps = {
	isEditing: boolean;
	onStartEdit: () => void;
	onCancelEdit: () => void;
	onOpenExternal: () => void;
	openDisabled: boolean;
	onSave: () => void;
	saveDisabled: boolean;
	saving: boolean;
	loading: boolean;
	onDelete: () => void;
	deleteDisabled: boolean;
	deleting: boolean;
	onViewHistory?: () => void;
	historyDisabled?: boolean;
	onAnalyze?: () => void;
	onTranslate?: () => void;
};

/** Page editor header action buttons (save, delete, analyze, translate). */
const PageEditorHeaderActions: React.FC<PageEditorHeaderActionsProps> = ({
	isEditing,
	onStartEdit,
	onCancelEdit,
	onOpenExternal,
	openDisabled,
	onSave,
	saveDisabled,
	saving,
	loading,
	onDelete,
	deleteDisabled,
	deleting,
	onViewHistory,
	historyDisabled = false,
	onAnalyze,
	onTranslate
}) => (
	<div className="flex flex-wrap items-center gap-2">
		{isEditing ? (
			<>
				{onViewHistory && (
					<button
						type="button"
						onClick={onViewHistory}
						disabled={historyDisabled}
						className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						<FiClock className="mr-2" /> Previous JSON
					</button>
				)}
				<button
					type="button"
					onClick={onDelete}
					disabled={deleteDisabled}
					className="inline-flex items-center rounded-full border border-rose-200 px-4 py-1.5 text-sm font-semibold text-rose-700 shadow-sm transition hover:border-rose-300 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{deleting ? (
						<>
							<LoadingSpinner size="sm" inline />
							<span className="ml-2">Deleting…</span>
						</>
					) : (
						<>
							<FiTrash2 className="mr-2" /> Delete
						</>
					)}
				</button>
				<button
					type="button"
					onClick={onOpenExternal}
					disabled={openDisabled}
					className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					<FiExternalLink className="mr-2" /> Open
				</button>
				<button
					type="button"
					onClick={onSave}
					disabled={saveDisabled}
					className="inline-flex items-center rounded-full bg-slate-900 px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
				>
					{saving ? (
						<>
							<LoadingSpinner size="sm" inline />
							<span className="ml-2">Saving…</span>
						</>
					) : (
						<>
							<FiSave className="mr-2" /> Save
						</>
					)}
				</button>
				<button
					type="button"
					onClick={onCancelEdit}
					disabled={loading}
					className="inline-flex items-center rounded-full border border-slate-200 px-4 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					Cancel
				</button>
			</>
		) : (
			<>
				{onAnalyze && (
					<button
						type="button"
						onClick={onAnalyze}
						disabled={loading}
						className="inline-flex items-center rounded-full border border-purple-200 bg-purple-50 px-4 py-1.5 text-sm font-semibold text-purple-700 shadow-sm transition hover:border-purple-300 hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-60"
					>
						<FiZap className="mr-2" /> Analyze
					</button>
				)}
				{onTranslate && (
					<button
						type="button"
						onClick={onTranslate}
						disabled={loading}
						className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-sm font-semibold text-blue-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
					>
						<FiGlobe className="mr-2" /> Translate
					</button>
				)}
				{onViewHistory && (
					<button
						type="button"
						onClick={onViewHistory}
						disabled={historyDisabled}
						className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						<FiClock className="mr-2" /> Previous JSON
					</button>
				)}
				<button
					type="button"
					onClick={onStartEdit}
					disabled={loading}
					className="inline-flex items-center rounded-full border border-slate-200 px-4 py-1.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					Enable editing
				</button>
			</>
		)}
		{loading && <span className="sr-only">Loading</span>}
	</div>
);

export default PageEditorHeaderActions;
